import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useUpdateBillingPlan } from '~entities/payment/api/hourly-tuition/update-hourly-tuition-billing';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';

export interface BillingRangeResponse {
  _id?: string;
  minMinute: number;
  maxMinute: number;
  flatRate: number;
}

export interface BillingPlanResponse {
  _id: string;
  name: string;
  studioId: string;
  ranges: Array<BillingRangeResponse>;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface EditBillingPlanDialogProps {
  plan: BillingPlanResponse | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedPlan: BillingPlanResponse) => void;
}

const rangeSchema = z.object({
  minMinute: z.number().int().min(0, 'Minimum minutes must be at least 0'),
  maxMinute: z
    .number()
    .int()
    .min(1, 'Maximum minutes must be at least 1')
    .refine((val) => val > 0, 'Maximum minutes must be greater than 0'),
  flatRate: z.number().min(0, 'Flat rate must be at least 0'),
});

const formSchema = z.object({
  name: z.string().min(1, 'Plan name is required'),
  ranges: z
    .array(rangeSchema)
    .min(1, 'At least one range is required')
    .refine(
      (ranges) => {
        for (let i = 0; i < ranges.length - 1; i++) {
          const currentRange = ranges[i];
          const nextRange = ranges[i + 1];
          if (
            currentRange?.maxMinute &&
            nextRange?.minMinute &&
            currentRange.maxMinute >= nextRange.minMinute
          )
            return false;
        }
        return true;
      },
      { message: 'Ranges must not overlap and should be in ascending order' },
    ),
});

type FormValues = z.infer<typeof formSchema>;

export function EditBillingPlanDialog({
  plan,
  isOpen,
  onClose,
  onSave,
}: EditBillingPlanDialogProps) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      ranges: [],
    },
  });

  const { mutateAsync: updatePlan, isPending } = useUpdateBillingPlan();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ranges',
  });

  useEffect(() => {
    if (plan) {
      reset({
        name: plan.name,
        ranges: plan.ranges.map((r) => ({
          minMinute: r.minMinute,
          maxMinute: r.maxMinute,
          flatRate: r.flatRate,
        })),
      });
    }
  }, [plan, reset]);

  const addRange = () => {
    const lastRange = fields[fields.length - 1];
    append({
      minMinute: lastRange?.maxMinute ? lastRange.maxMinute + 1 : 0,
      maxMinute: lastRange?.maxMinute ? lastRange.maxMinute + 30 : 30,
      flatRate: 0,
    });
  };

  const onSubmit = async (params: FormValues) => {
    if (!plan) return;

    try {
      const updatedPlan = await updatePlan({
        planId: plan._id,
        params,
      });

      onSave(updatedPlan.data as BillingPlanResponse);
      onClose();
      toast.success('Billing plan updated successfully');
    } catch (error) {
      console.error('Error updating billing plan:', error);
      toast.error('Failed to update the billing plan. Please try again.');
    }
  };

  if (!plan) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className='max-h-[80vh] max-w-2xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Edit Billing Plan</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
          <div className='space-y-2'>
            <Label htmlFor='name'>Plan Name</Label>
            <Input
              id='name'
              {...register('name')}
              placeholder='Enter plan name'
            />
            {errors.name && (
              <p className='mt-1 text-sm text-red-500'>{errors.name.message}</p>
            )}
          </div>

          <div className='space-y-4'>
            <div className='flex items-center justify-between'>
              <h3 className='text-lg font-semibold'>Tuition Rate Ranges</h3>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={addRange}
                className='flex items-center gap-2 bg-transparent'
              >
                <Plus className='size-4' />
                Add Range
              </Button>
            </div>

            <div className='space-y-3'>
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className='grid grid-cols-10 items-end gap-4 rounded-lg border border-gray-200 p-3'
                >
                  <div className='col-span-3 space-y-2'>
                    <Label htmlFor={`ranges.${index}.minMinute`}>
                      Min Minutes
                    </Label>
                    <Input
                      type='number'
                      {...register(`ranges.${index}.minMinute`, {
                        valueAsNumber: true,
                      })}
                      placeholder='0'
                    />
                    {errors.ranges?.root?.message && (
                      <p className='mt-2 text-sm text-red-500'>
                        {errors.ranges.root.message}
                      </p>
                    )}
                  </div>
                  <div className='col-span-3 space-y-2'>
                    <Label htmlFor={`ranges.${index}.maxMinute`}>
                      Max Minutes
                    </Label>
                    <Input
                      type='number'
                      {...register(`ranges.${index}.maxMinute`, {
                        valueAsNumber: true,
                      })}
                      placeholder='0'
                    />
                    {errors.ranges?.[index]?.maxMinute && (
                      <p className='mt-1 text-sm text-red-500'>
                        {errors.ranges[index].maxMinute.message}
                      </p>
                    )}
                  </div>
                  <div className='col-span-3 space-y-2'>
                    <Label htmlFor={`ranges.${index}.flatRate`}>
                      Flat Rate ($)
                    </Label>
                    <Input
                      type='number'
                      step='0.01'
                      {...register(`ranges.${index}.flatRate`, {
                        valueAsNumber: true,
                      })}
                      placeholder='0.00'
                    />
                    {errors.ranges?.[index]?.flatRate && (
                      <p className='mt-1 text-sm text-red-500'>
                        {errors.ranges[index].flatRate.message}
                      </p>
                    )}
                  </div>
                  <div className='flex h-full items-center justify-center'>
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      onClick={() => {
                        remove(index);
                      }}
                      disabled={fields.length === 1}
                      className='size-6 border-none p-0 text-red-600 hover:bg-transparent hover:text-red-700'
                    >
                      <Trash2 className='size-4' />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {errors.ranges && typeof errors.ranges.message === 'string' && (
              <p className='mt-2 text-sm text-red-500'>
                {errors.ranges.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type='submit' disabled={isPending}>
              {isPending && <Loader2 className='animate-spin' />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
